import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  Dialog,
  TextField,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { TimezoneSelect } from '@shared/components/forms/TimezoneSelect';
import { ActivityTypeSelect } from '@shared/components/forms/ActivityTypeSelect';
import { useUpdateActivity } from '../hooks/useActivityMutations';
import { ErrorAPI } from '@shared/components/ui/ErrorAPI';
import { useStyles } from '../styles/ModificationModal.styles';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const ModificationModal = (props) => {
  const { openModal, handleCloseModal, activity } = props;
  const classes = useStyles();
  const updateActivityMutation = useUpdateActivity();
  const [activityTypeId, setActivityTypeId] = useState(activity.activity_type_id);
  const [specialActivityName, setSpecialActivityName] = useState(activity.special_activity_name);
  const [startedAt, setStartedAt] = useState(dayjs.utc(activity.started_at).local().format('YYYY-MM-DDTHH:mm'));
  const [endedAt, setEndedAt] = useState(dayjs.utc(activity.ended_at).local().format('YYYY-MM-DDTHH:mm'));
  const [timezoneId, setTimezoneId] = useState(activity.timezone_id);
  const [reinforcement, setReinforcement] = useState(activity.reinforcement);
  const [negativeDurationAlert, setNegativeDurationAlert] = useState(false);

  // Reset form states when activity changes
  useEffect(() => {
    if (activity) {
      setActivityTypeId(activity.activity_type_id);
      setSpecialActivityName(activity.special_activity_name);
      setStartedAt(dayjs.utc(activity.started_at).local().format('YYYY-MM-DDTHH:mm'));
      setEndedAt(dayjs.utc(activity.ended_at).local().format('YYYY-MM-DDTHH:mm'));
      setTimezoneId(activity.timezone_id);
      setReinforcement(activity.reinforcement);
      setNegativeDurationAlert(false);
    }
  }, [activity]);

  const handleActivityTypeId = (event) => {
    setActivityTypeId(event.target.value);
  };

  const handleSpecialActivityName = (event) => {
    setSpecialActivityName(event.target.value);
  };

  const handleStart = (event) => {
    setNegativeDurationAlert(false);
    const value = event.target.value;
    const end = dayjs(endedAt);
    const start = dayjs(value);
    const durationMinutes = end.diff(start, 'minute');
    if (durationMinutes < 0) {
      setNegativeDurationAlert(true);
    }
    setStartedAt(event.target.value);
  };

  const handleEnd = (event) => {
    setNegativeDurationAlert(false);
    const value = event.target.value;
    const start = dayjs(startedAt);
    const end = dayjs(value);
    const durationMinutes = end.diff(start, 'minute');

    if (durationMinutes < 0) {
      setNegativeDurationAlert(true);
    }
    setEndedAt(value);
  };

  const handleTimezoneId = (event) => {
    setTimezoneId(event.target.value);
  };

  const handleReinforcement = (event) => {
    setReinforcement(event.target.checked);
  };

  const handleSaveAndClose = () => {
    const specialActivityLabel = activityTypeId === 4 ? specialActivityName : '';
    // Convert local datetime-local input to UTC timestamp
    const start = dayjs(startedAt).utc().unix();
    const end = dayjs(endedAt).utc().unix();

    updateActivityMutation.mutate({
      id: activity.id,
      end: end,
      start: start,
      manual: true,
      activityTypeId: activityTypeId,
      specialActivityLabel: specialActivityLabel,
      timezoneId: timezoneId,
      reinforcement: reinforcement,
    }, {
      onSuccess: () => {
        handleCloseModal();
      },
    });
  };

  return (
    <div>
      <Dialog open={openModal} onClose={handleCloseModal} aria-labelledby="form-dialog-title">
        <DialogTitle disableTypography>
          <Typography variant="h6" className={classes.title}>
            Modifier l'activité suivante
          </Typography>

          <IconButton aria-label="close" className={classes.closeButton} onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <ErrorAPI
          errorAPI={updateActivityMutation.isError}
          setErrorAPI={() => updateActivityMutation.reset()}
          message={
            'Un problème est survenu lors de la modification, veuillez recharger la page ou réessayer plus tard.'
          }
        />
        <DialogContent dividers>
          
          <ActivityTypeSelect activityTypeId={activityTypeId} handleActivityTypeId={handleActivityTypeId} />

          {activityTypeId === 4 && (
            <TextField
              margin="dense"
              id="activityType"
              variant="outlined"
              label="Intitulé de l'activité"
              value={specialActivityName}
              onChange={handleSpecialActivityName}
              fullWidth
            />
          )}

          <div className={classes.rowDate}>
            <TextField
              id="ActivityStart"
              label="Début de l'activité"
              type="datetime-local"
              margin="dense"
              value={startedAt}
              onChange={handleStart}
              className={classes.spacing}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              id="ActivityEnd"
              label="Fin de l'activité"
              type="datetime-local"
              margin="dense"
              value={endedAt}
              onChange={handleEnd}
              error={negativeDurationAlert}
              helperText={negativeDurationAlert ? 'Fin inférieure au début' : ' '}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
          <div className={classes.rowTimezone}>
            <div className={classes.colTimezone}>
              <TimezoneSelect timezoneId={timezoneId} handleTimezoneId={handleTimezoneId} />
            </div>
            <FormControlLabel
              control={
                <Checkbox checked={reinforcement} onChange={handleReinforcement} name="reinforcement" color="primary" />
              }
              label="Activité de renfort"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary" disabled={updateActivityMutation.isPending}>
            Annuler
          </Button>
          <Button
            onClick={handleSaveAndClose}
            disabled={negativeDurationAlert || updateActivityMutation.isPending}
            color="primary"
          >
            {updateActivityMutation.isPending ? 'Enregistrement...' : 'Enregistrer la correction'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
